import React from 'react';

export type StateKey = 'baseline' | 'withdrawal' | 'agitation' | 'numbness' | 'fragmentation';

interface DriftState {
  bRGB: [number, number, number];
  wRGB: [number, number, number];
  cRGB: [number, number, number];
  bOp: number; bOffX: number; bOffY: number;
  bSc: number; wSc: number; wOp: number;
  cSc: number; cOp: number;
  oD: number; mD: number; cD: number;
  bl: number;
}

const STATES: Record<StateKey, DriftState> = {
  baseline: {
    bRGB: [20, 70, 210], wRGB: [215, 138, 32], cRGB: [255, 244, 215],
    bOp: 0.88, bOffX: 28, bOffY: 3, bSc: 1.00, wSc: 1.00, wOp: 0.95, cSc: 1.00, cOp: 1.0,
    oD: 18, mD: 12, cD: 9, bl: 1.00,
  },
  withdrawal: {
    bRGB: [18, 62, 205], wRGB: [200, 128, 25], cRGB: [250, 232, 175],
    bOp: 0.82, bOffX: 48, bOffY: 4, bSc: 1.22, wSc: 0.70, wOp: 0.72, cSc: 0.62, cOp: 0.82,
    oD: 24, mD: 18, cD: 13, bl: 1.18,
  },
  agitation: {
    bRGB: [40, 55, 228], wRGB: [232, 92, 25], cRGB: [255, 238, 195],
    bOp: 0.90, bOffX: 24, bOffY: 2, bSc: 0.98, wSc: 0.96, wOp: 0.94, cSc: 0.96, cOp: 1.0,
    oD: 5, mD: 3, cD: 2, bl: 0.92,
  },
  numbness: {
    bRGB: [88, 108, 148], wRGB: [175, 152, 105], cRGB: [225, 218, 202],
    bOp: 0.62, bOffX: 22, bOffY: 2, bSc: 1.08, wSc: 0.88, wOp: 0.56, cSc: 0.80, cOp: 0.62,
    oD: 38, mD: 38, cD: 38, bl: 1.45,
  },
  fragmentation: {
    bRGB: [16, 108, 192], wRGB: [212, 135, 35], cRGB: [248, 232, 182],
    bOp: 0.76, bOffX: 44, bOffY: 3, bSc: 1.12, wSc: 0.88, wOp: 0.80, cSc: 0.82, cOp: 0.86,
    oD: 13, mD: 10, cD: 8, bl: 1.12,
  },
};

export const ORB_KEYFRAMES = `
  @keyframes drift-orb-outer-base {
    0%   { transform: translate(-50%,-50%) translate(28px,3px); }
    30%  { transform: translate(-50%,-50%) translate(40px,-10px); }
    65%  { transform: translate(-50%,-50%) translate(18px,17px); }
    100% { transform: translate(-50%,-50%) translate(28px,3px); }
  }
  @keyframes drift-orb-outer-w {
    0%   { transform: translate(-50%,-50%) translate(48px,4px); }
    30%  { transform: translate(-50%,-50%) translate(60px,-9px); }
    65%  { transform: translate(-50%,-50%) translate(38px,18px); }
    100% { transform: translate(-50%,-50%) translate(48px,4px); }
  }
  @keyframes drift-orb-outer-a {
    0%  { transform: translate(-50%,-50%) translate(24px,0px); }
    14% { transform: translate(-50%,-50%) translate(34px,-13px); }
    32% { transform: translate(-50%,-50%) translate(14px,12px); }
    50% { transform: translate(-50%,-50%) translate(30px,-7px); }
    68% { transform: translate(-50%,-50%) translate(16px,15px); }
    84% { transform: translate(-50%,-50%) translate(27px,-4px); }
    100%{ transform: translate(-50%,-50%) translate(24px,0px); }
  }
  @keyframes drift-orb-outer-n {
    0%   { transform: translate(-50%,-50%) translate(22px,2px); }
    50%  { transform: translate(-50%,-50%) translate(28px,6px); }
    100% { transform: translate(-50%,-50%) translate(22px,2px); }
  }
  @keyframes drift-orb-outer-f {
    0%  { transform: translate(-50%,-50%) translate(44px,-12px); }
    25% { transform: translate(-50%,-50%) translate(18px,21px); }
    50% { transform: translate(-50%,-50%) translate(55px,6px); }
    75% { transform: translate(-50%,-50%) translate(10px,-18px); }
    100%{ transform: translate(-50%,-50%) translate(44px,-12px); }
  }
  @keyframes drift-orb-mid {
    0%  { transform: translate(-50%,-50%) translate(-3px,-2px); }
    28% { transform: translate(-50%,-50%) translate(10px,7px); }
    58% { transform: translate(-50%,-50%) translate(-7px,9px); }
    82% { transform: translate(-50%,-50%) translate(6px,-6px); }
    100%{ transform: translate(-50%,-50%) translate(-3px,-2px); }
  }
  @keyframes drift-orb-mid-a {
    0%  { transform: translate(-50%,-50%) translate(0px,0px); }
    14% { transform: translate(-50%,-50%) translate(14px,-12px); }
    28% { transform: translate(-50%,-50%) translate(-12px,11px); }
    45% { transform: translate(-50%,-50%) translate(12px,13px); }
    62% { transform: translate(-50%,-50%) translate(-11px,-9px); }
    80% { transform: translate(-50%,-50%) translate(13px,6px); }
    100%{ transform: translate(-50%,-50%) translate(0px,0px); }
  }
  @keyframes drift-orb-mid-f {
    0%  { transform: translate(-50%,-50%) translate(-17px,10px); }
    33% { transform: translate(-50%,-50%) translate(15px,-15px); }
    66% { transform: translate(-50%,-50%) translate(-12px,18px); }
    100%{ transform: translate(-50%,-50%) translate(-17px,10px); }
  }
  @keyframes drift-orb-core {
    0%  { transform: translate(-50%,-50%) translate(0px,0px); }
    22% { transform: translate(-50%,-50%) translate(8px,-7px); }
    48% { transform: translate(-50%,-50%) translate(-7px,8px); }
    72% { transform: translate(-50%,-50%) translate(7px,5px); }
    100%{ transform: translate(-50%,-50%) translate(0px,0px); }
  }
  @keyframes drift-orb-core-a {
    0%  { transform: translate(-50%,-50%) translate(0px,0px); }
    20% { transform: translate(-50%,-50%) translate(10px,-10px); }
    40% { transform: translate(-50%,-50%) translate(-11px,9px); }
    60% { transform: translate(-50%,-50%) translate(9px,11px); }
    80% { transform: translate(-50%,-50%) translate(-9px,-8px); }
    100%{ transform: translate(-50%,-50%) translate(0px,0px); }
  }
  @keyframes drift-orb-core-f {
    0%  { transform: translate(-50%,-50%) translate(12px,-18px); }
    28% { transform: translate(-50%,-50%) translate(-15px,10px); }
    55% { transform: translate(-50%,-50%) translate(18px,13px); }
    80% { transform: translate(-50%,-50%) translate(-10px,-13px); }
    100%{ transform: translate(-50%,-50%) translate(12px,-18px); }
  }
  @keyframes drift-orb-frag-split {
    0%  { transform: translate(-50%,-50%) translate(-9px,15px); }
    40% { transform: translate(-50%,-50%) translate(15px,-12px); }
    75% { transform: translate(-50%,-50%) translate(-12px,-8px); }
    100%{ transform: translate(-50%,-50%) translate(-9px,15px); }
  }
  @keyframes drift-ghost-pulse {
    0%,100%{ opacity: 0.13; transform: translate(-50%,-50%) scale(1.00); }
    50%    { opacity: 0.06; transform: translate(-50%,-50%) scale(1.03); }
  }
`;

function getAnims(k: StateKey, oD: number, mD: number, cD: number) {
  const outerName =
    k === 'withdrawal'    ? 'drift-orb-outer-w' :
    k === 'agitation'     ? 'drift-orb-outer-a' :
    k === 'numbness'      ? 'drift-orb-outer-n' :
    k === 'fragmentation' ? 'drift-orb-outer-f' : 'drift-orb-outer-base';
  const midName =
    k === 'agitation'     ? 'drift-orb-mid-a' :
    k === 'fragmentation' ? 'drift-orb-mid-f' : 'drift-orb-mid';
  const coreName =
    k === 'agitation'     ? 'drift-orb-core-a' :
    k === 'fragmentation' ? 'drift-orb-core-f' : 'drift-orb-core';
  return {
    outer: `${outerName} ${oD}s ease-in-out infinite`,
    mid:   `${midName} ${mD}s ease-in-out infinite`,
    core:  `${coreName} ${cD}s ease-in-out infinite`,
    point: `drift-orb-core 7s ease-in-out infinite`,
    split: `drift-orb-frag-split 13s ease-in-out infinite`,
  };
}

interface LayerProps {
  w: number; h: number;
  br: string; bg: string;
  blur: number; anim: string;
}
const Layer: React.FC<LayerProps> = ({ w, h, br, bg, blur, anim }) => (
  <div style={{
    position: 'absolute', top: '50%', left: '50%',
    width: w, height: h, borderRadius: br,
    background: bg,
    filter: `blur(${blur}px)`,
    animation: anim,
    willChange: 'transform',
    transition: 'width 2.8s ease-in-out, height 2.8s ease-in-out, filter 2.8s ease-in-out, background 2.8s ease-in-out, opacity 2.8s ease-in-out',
  }} />
);

interface OrbProps {
  stateKey: StateKey;
}

export const Orb: React.FC<OrbProps> = ({ stateKey }) => {
  const s = STATES[stateKey];
  const [r, g, b] = s.bRGB;
  const [wr, wg, wb] = s.wRGB;
  const op = (v: number, f: number) => +(v * f).toFixed(2);
  const anims = getAnims(stateKey, s.oD, s.mD, s.cD);

  const oW = Math.round(360 * s.bSc), oH = Math.round(276 * s.bSc);
  const mW = Math.round(210 * s.wSc), mH = Math.round(194 * s.wSc);
  const cW = Math.round(112 * s.cSc), cH = Math.round(104 * s.cSc);
  const bPx = Math.round(34 * s.bl), mPx = Math.round(17 * s.bl), cPx = Math.round(8 * s.bl);
  const isF = stateKey === 'fragmentation';

  return (
    <div style={{ position: 'relative', width: 0, height: 0 }}>
      {/* Ghost ring */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 320, height: 320, borderRadius: '50%',
        border: '1px solid rgba(20,70,210,0.14)',
        animation: 'drift-ghost-pulse 6s ease-in-out infinite',
        willChange: 'transform, opacity',
        transition: 'opacity 2.8s ease-in-out',
      }} />

      {/* Blue outer field */}
      <Layer w={oW} h={oH}
        br="54% 46% 52% 48% / 44% 56% 44% 56%"
        bg={`radial-gradient(ellipse at 34% 52%,
          rgba(${r},${g},${b},${s.bOp}) 0%,
          rgba(${r+12},${g+18},${b},${op(s.bOp,0.62)}) 26%,
          rgba(${r+22},${g+32},${b},${op(s.bOp,0.28)}) 52%,
          rgba(${r+30},${g+44},${b},${op(s.bOp,0.08)}) 72%,
          transparent 88%)`}
        blur={bPx} anim={anims.outer}
      />

      {/* Warm mid */}
      <Layer w={mW} h={mH}
        br="46% 54% 44% 56% / 55% 45% 53% 47%"
        bg={`radial-gradient(ellipse at 46% 48%,
          rgba(${wr},${wg},${wb},${s.wOp}) 0%,
          rgba(${wr+10},${wg+18},${wb+22},${op(s.wOp,0.78)}) 26%,
          rgba(${wr+18},${wg+32},${wb+40},${op(s.wOp,0.44)}) 52%,
          transparent 86%)`}
        blur={mPx} anim={anims.mid}
      />

      {/* Bright core */}
      <Layer w={cW} h={cH} br="50%"
        bg={`radial-gradient(ellipse at center,
          rgba(${s.cRGB[0]},${s.cRGB[1]},${s.cRGB[2]},${s.cOp}) 0%,
          rgba(${s.cRGB[0]-8},${s.cRGB[1]-22},${Math.max(0,s.cRGB[2]-55)},${op(s.cOp,0.82)}) 28%,
          transparent 78%)`}
        blur={cPx} anim={anims.core}
      />

      {/* Light point */}
      <Layer w={36} h={32} br="50%"
        bg={`radial-gradient(ellipse at center,
          rgba(255,252,238,${s.cOp}) 0%,
          rgba(255,242,210,${op(s.cOp,0.7)}) 44%,
          transparent 80%)`}
        blur={3} anim={anims.point}
      />

      {/* Fragmentation secondary core */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 44, height: 40, borderRadius: '50%',
        background: 'radial-gradient(ellipse at center, rgba(175,198,240,0.48) 0%, transparent 74%)',
        filter: 'blur(12px)',
        animation: anims.split,
        willChange: 'transform',
        opacity: isF ? 1 : 0,
        transition: 'opacity 2.8s ease',
      }} />
    </div>
  );
};
